import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BellIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import SettingsInfoNotice from './SettingsInfoNotice';

const DEFAULT_NOTIFICATIONS = {
  emailEnabled: true,
  subscriptionReminders: true,
  paymentReminders: true,
  maintenanceAlerts: true,
};

function stripSmsFromNotifications(notifications) {
  if (!notifications || typeof notifications !== 'object') return { ...DEFAULT_NOTIFICATIONS };
  const { smsEnabled, ...rest } = notifications;
  return { ...DEFAULT_NOTIFICATIONS, ...rest };
}

const NotificationSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATIONS);

  useEffect(() => {
    if (settings?.notifications) {
      setNotificationSettings(stripSmsFromNotifications(settings.notifications));
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newNotificationSettings = stripSmsFromNotifications({
      ...notificationSettings,
      [field]: value,
    });
    setNotificationSettings(newNotificationSettings);
    onSettingsChange({ ...settings, notifications: newNotificationSettings });
    onHasChanges(true);
  };

  const notificationTypes = [
    {
      key: 'subscriptionReminders',
      name: "Rappels d'inscription",
      description: "Alertes avant la fin d'une session ou d'un groupe",
      icon: CalendarDaysIcon,
      color: 'text-blue-400',
    },
    {
      key: 'paymentReminders',
      name: 'Rappels de paiement',
      description: 'Notifications pour les échéances et soldes impayés',
      icon: EnvelopeIcon,
      color: 'text-green-400',
    },
    {
      key: 'maintenanceAlerts',
      name: 'Alertes de formation',
      description: 'Changements de planning, annulations ou informations urgentes',
      icon: AcademicCapIcon,
      color: 'text-orange-400',
    },
  ];

  const getActiveNotificationsCount = () =>
    notificationTypes.filter((type) => notificationSettings[type.key]).length;

  return (
    <div className="space-y-6">
      <SettingsInfoNotice variant="warning" title="Préférences enregistrées">
        <p>
          Ces options sont sauvegardées pour votre établissement. L&apos;envoi automatique par email
          sera activé lorsque le service de notifications sera connecté.
        </p>
      </SettingsInfoNotice>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900/75"
      >
        <div className="mb-4 flex items-center gap-3">
          <EnvelopeIcon className="h-5 w-5 text-blue-400" />
          <h4 className="font-semibold text-slate-900 dark:text-slate-50">Notifications Email</h4>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-700 dark:text-slate-300">Activer les notifications par email</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Envoyer des emails pour les alertes importantes
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('emailEnabled', !notificationSettings.emailEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                notificationSettings.emailEnabled ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notificationSettings.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div
            className={`rounded-xl bg-slate-100/80 p-3 dark:bg-slate-800/40 ${
              notificationSettings.emailEnabled ? 'border border-green-500/20' : 'border border-red-500/20'
            }`}
          >
            <div className="mb-2 flex items-center gap-2">
              {notificationSettings.emailEnabled ? (
                <CheckCircleIcon className="h-4 w-4 text-green-400" />
              ) : (
                <XCircleIcon className="h-4 w-4 text-red-400" />
              )}
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {notificationSettings.emailEnabled ? 'Activé' : 'Désactivé'}
              </p>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              {notificationSettings.emailEnabled
                ? 'Les notifications seront envoyées par email'
                : 'Aucune notification par email ne sera envoyée'}
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900/75"
      >
        <div className="mb-4 flex items-center gap-3">
          <BellIcon className="h-5 w-5 text-yellow-400" />
          <h4 className="font-semibold text-slate-900 dark:text-slate-50">Types de notifications</h4>
        </div>

        <div className="space-y-4">
          {notificationTypes.map((type, index) => {
            const Icon = type.icon;
            return (
              <motion.div
                key={type.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="flex items-center justify-between rounded-xl bg-slate-100/80 p-4 dark:bg-slate-800/40"
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${type.color}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{type.name}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">{type.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange(type.key, !notificationSettings[type.key])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notificationSettings[type.key] ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notificationSettings[type.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-2xl border border-slate-200 bg-slate-100/80 p-6 backdrop-blur-lg dark:border-slate-600 dark:bg-slate-800/40"
      >
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-50">Résumé des notifications</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-xl bg-slate-100/80 p-4 text-center dark:bg-slate-800/40">
            <EnvelopeIcon className="mx-auto mb-2 h-6 w-6 text-blue-400" />
            <div className="text-sm font-bold text-slate-900 dark:text-slate-50">
              {notificationSettings.emailEnabled ? 'Activé' : 'Désactivé'}
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400">Email</div>
          </div>
          <div className="rounded-xl bg-slate-100/80 p-4 text-center dark:bg-slate-800/40">
            <BellIcon className="mx-auto mb-2 h-6 w-6 text-yellow-400" />
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{getActiveNotificationsCount()}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">Types actifs</div>
          </div>
          <div className="rounded-xl bg-slate-100/80 p-4 text-center dark:bg-slate-800/40">
            <CheckCircleIcon className="mx-auto mb-2 h-6 w-6 text-purple-400" />
            <div className="text-sm font-bold text-slate-900 dark:text-slate-50">
              {notificationSettings.emailEnabled ? 'Configuré' : 'Inactif'}
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400">État global</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default NotificationSettings;
